import { logger } from '@tuic/logger';

import { XI_CAFE_DOMAIN } from './captive-portal-url';

import type { XiCafeBookingsListResponse } from './types';

/** XiCafe REST API base URL. The API runs on port 8085; the default port serves the captive portal. */
export const XI_CAFE_API_BASE_URL = `${XI_CAFE_DOMAIN}:8085/api/v2`;

const REQUEST_TIMEOUT_MS = 10_000;

const LOG_PREFIX = '[xi-cafe]';

const emptyFailure = (error: string): XiCafeBookingsListResponse => ({
  data: [],
  error,
  success: false,
});

const isXiCafeBookingsListResponse = (
  value: unknown,
): value is XiCafeBookingsListResponse => {
  if (value === null || typeof value !== 'object') return false;
  if (!('success' in value) || typeof value.success !== 'boolean') return false;
  if (!('error' in value) || typeof value.error !== 'string') return false;
  if (!('data' in value) || !Array.isArray(value.data)) return false;
  return true;
};

/**
 * Fetch tariff bookings for a guest account from the XiCafe API.
 *
 * Single endpoint replaces the GetSlash `clientStatus` + `clientBookingList`
 * pair. Must be called from the browser — port 8085 on internet.meinschiff.de
 * is only reachable on the ship's local network.
 *
 * Errors are swallowed and returned as `{ success: false, error }`; the hook
 * that consumes this surfaces them as a graceful "Connected" fallback. This
 * function never throws.
 *
 * @param mpi - Guest account identifier (MPI) as a string
 * @param bearerToken - XiCafe JWT for the Authorization header, or null if
 *   unavailable (the API call will still happen but will likely 401)
 */
export const xiCafeBookingsList = async (
  mpi: string,
  bearerToken: string | null,
): Promise<XiCafeBookingsListResponse> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  try {
    const response = await fetch(`${XI_CAFE_API_BASE_URL}/bookings/list`, {
      body: JSON.stringify({
        account: mpi,
        includeSessions: true,
        productTypes: ['INTERNET'],
      }),
      headers,
      method: 'POST',
      signal: controller.signal,
    });

    const text = await response.text();

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      logger.warn(
        `${LOG_PREFIX} bookings/list JSON parse error. Status: ${response.status}, body: ${text.substring(0, 200)}`,
      );
      return emptyFailure(`Invalid JSON response (HTTP ${response.status})`);
    }

    if (!isXiCafeBookingsListResponse(parsed)) {
      logger.warn(
        `${LOG_PREFIX} bookings/list unexpected response shape. Status: ${response.status}, body: ${text.substring(0, 200)}`,
      );
      return emptyFailure(
        `Unexpected response shape (HTTP ${response.status})`,
      );
    }

    if (!parsed.success) {
      logger.warn(
        `${LOG_PREFIX} bookings/list returned success=false: ${parsed.error}`,
      );
    }

    return parsed;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    logger.warn(`${LOG_PREFIX} bookings/list request failed: ${message}`);
    return emptyFailure(message);
  } finally {
    clearTimeout(timeoutId);
  }
};
